/**
 * SmartLink publisher whitelist / blacklist page — operator UI.
 *
 * Renders:
 *   - Mode toggle: Allow list / Deny list. Flips the default `list_kind`
 *     used by the add form (see inferListKindFromMode).
 *   - Add-entry form: publisher_id input; the list_kind is inferred from
 *     the current mode, so operators only pick rows once per mode.
 *   - Current entries table: results from GET /by-smartlink/:id, one row
 *     per entry with an inline DELETE button that calls DELETE /:id.
 *   - Access tester: {smartlink_id, publisher_id} → POST /check → a badge
 *     showing "Allowed" or "Blocked" alongside the API-returned reason.
 *
 * Data: TanStack Query for fetch + mutations. Toast via Sonner for feedback.
 * Form: local state + the `.safeParse` schemas exported from `./api.ts`.
 * No react-hook-form: keeps the lane to just these owned files.
 */

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
	addEntry,
	addEntrySchema,
	checkAccess,
	checkSchema,
	deleteEntry,
	fetchBySmartlink,
	inferListKindFromMode,
	type CheckResponse,
	type PubListEntry,
	type PubListKind,
	type PubListMode,
	pubListQueryKeys,
	toggleMode,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens — zinc borders, 6–8px rounded, emerald→sky gradient reserved
// for the headline via `text-brand-gradient`. Light theme only, Inter via
// the scaffold's tailwind config.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const HINT = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs text-red-600";
const PRIMARY_BTN =
	"rounded-[6px] border border-emerald-600 bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const SECONDARY_BTN =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const GHOST_DANGER_BTN =
	"rounded-[6px] border border-transparent px-2 py-1 text-xs font-medium text-red-600 hover:border-red-200 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";

export interface SmartlinkPubListPageProps {
	initialSmartlinkId?: string;
}

export function SmartlinkPubListPage(props: SmartlinkPubListPageProps) {
	const queryClient = useQueryClient();
	const [smartlinkId, setSmartlinkId] = useState(
		props.initialSmartlinkId ?? "",
	);
	const [mode, setMode] = useState<PubListMode>("allow");

	const trimmed = smartlinkId.trim();
	const canQuery = trimmed.length > 0;

	// ---------------- Entries query ----------------
	const entriesQuery = useQuery({
		queryKey: pubListQueryKeys.bySmartlink(trimmed),
		queryFn: () => fetchBySmartlink(trimmed),
		enabled: canQuery,
	});

	const entries = entriesQuery.data?.entries ?? [];

	// ---------------- Delete mutation ----------------
	const deleteMutation = useMutation({
		mutationFn: (id: string) => deleteEntry(id),
		onSuccess: (_void, id) => {
			toast.success("Entry removed.");
			queryClient.setQueryData(
				pubListQueryKeys.bySmartlink(trimmed),
				(prev: ReturnType<typeof fetchBySmartlink> extends Promise<infer R> ? R : never) => {
					if (!prev) return prev;
					const nextEntries = prev.entries.filter((e: PubListEntry) => e.id !== id);
					return {
						...prev,
						entries: nextEntries,
						allow: prev.allow.filter((e: PubListEntry) => e.id !== id),
						deny: prev.deny.filter((e: PubListEntry) => e.id !== id),
						mode:
							nextEntries.some((e: PubListEntry) => e.list_kind === "allow")
								? "allow"
								: nextEntries.some((e: PubListEntry) => e.list_kind === "deny")
									? "deny"
									: "open",
					};
				},
			);
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to remove entry.");
		},
	});

	// ---------------- Add mutation ----------------
	const addMutation = useMutation({
		mutationFn: addEntry,
		onSuccess: () => {
			toast.success("Entry added.");
			void queryClient.invalidateQueries({
				queryKey: pubListQueryKeys.bySmartlink(trimmed),
			});
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to add entry.");
		},
	});

	// ---------------- Check mutation ----------------
	const [checkResult, setCheckResult] = useState<CheckResponse | null>(null);
	const checkMutation = useMutation({
		mutationFn: checkAccess,
		onSuccess: (res) => {
			setCheckResult(res);
		},
		onError: (err: Error) => {
			setCheckResult(null);
			toast.error(err.message || "Unable to evaluate access.");
		},
	});

	// ---------------- Derived ----------------
	const modeDescription = useMemo(() => {
		if (mode === "allow") {
			return "Allow list: only listed publishers may drive traffic.";
		}
		return "Deny list: listed publishers are blocked; everyone else is allowed.";
	}, [mode]);

	return (
		<section className="flex flex-col gap-6">
			<SmartlinkIdCard
				value={smartlinkId}
				onChange={setSmartlinkId}
				loading={entriesQuery.isFetching}
				locked={Boolean(props.initialSmartlinkId)}
			/>
			<ModeCard
				mode={mode}
				onToggle={() => setMode((m) => toggleMode(m))}
				description={modeDescription}
			/>
			<AddEntryCard
				smartlinkId={trimmed}
				mode={mode}
				busy={addMutation.isPending}
				onSubmit={(values) => {
					addMutation.mutate(values);
				}}
			/>
			<EntriesTableCard
				entries={entries}
				busyDeleteId={deleteMutation.isPending ? deleteMutation.variables : null}
				onDelete={(id) => deleteMutation.mutate(id)}
				loading={entriesQuery.isLoading}
				canQuery={canQuery}
			/>
			<AccessTesterCard
				smartlinkId={trimmed}
				busy={checkMutation.isPending}
				result={checkResult}
				onSubmit={(values) => {
					checkMutation.mutate(values);
				}}
			/>
		</section>
	);
}

export default SmartlinkPubListPage;

// ---------------------------------------------------------------------------
// Section cards — broken out for readability. Purely presentational.
// ---------------------------------------------------------------------------

function SmartlinkIdCard(props: {
	value: string;
	onChange: (v: string) => void;
	loading?: boolean;
	locked?: boolean;
}) {
	return (
		<div className={`${CARD} flex flex-col gap-2 p-5`}>
			<label htmlFor="pub-list-smartlink-id" className={LABEL}>
				SmartLink ID
			</label>
			<input
				id="pub-list-smartlink-id"
				name="smartlink_id"
				type="text"
				autoComplete="off"
				placeholder="e.g. sl_9f21ab"
				className={INPUT}
				value={props.value}
				onChange={(e) => props.onChange(e.currentTarget.value)}
				disabled={props.locked}
			/>
			<div className="flex items-center justify-between">
				<p className={HINT}>
					All entries, the access tester, and the mode toggle scope to this
					SmartLink.
				</p>
				{props.loading && (
					<span className={HINT} aria-live="polite">
						Loading entries.
					</span>
				)}
			</div>
		</div>
	);
}

function ModeCard(props: {
	mode: PubListMode;
	onToggle: () => void;
	description: string;
}) {
	return (
		<div className={`${CARD} flex flex-col gap-3 p-5`}>
			<div className="flex flex-col gap-2">
				<span className={LABEL}>Mode</span>
				<div
					role="radiogroup"
					aria-label="List mode"
					className="inline-flex w-fit rounded-[6px] border border-zinc-200 bg-zinc-50 p-0.5"
				>
					<ModeButton
						active={props.mode === "allow"}
						onClick={() => {
							if (props.mode !== "allow") props.onToggle();
						}}
						label="Allow list"
					/>
					<ModeButton
						active={props.mode === "deny"}
						onClick={() => {
							if (props.mode !== "deny") props.onToggle();
						}}
						label="Deny list"
					/>
				</div>
				<p className={HINT}>{props.description}</p>
			</div>
		</div>
	);
}

function ModeButton(props: {
	active: boolean;
	onClick: () => void;
	label: string;
}) {
	const base =
		"rounded-[6px] px-3 py-1.5 text-sm font-medium transition-colors";
	const on = "bg-white text-zinc-900 shadow-[0_1px_2px_0_rgb(24_24_27/0.06)]";
	const off = "text-zinc-600 hover:text-zinc-900";
	return (
		<button
			type="button"
			role="radio"
			aria-checked={props.active}
			onClick={props.onClick}
			className={`${base} ${props.active ? on : off}`}
		>
			{props.label}
		</button>
	);
}

function AddEntryCard(props: {
	smartlinkId: string;
	mode: PubListMode;
	busy: boolean;
	onSubmit: (v: {
		smartlink_id: string;
		publisher_id: string;
		list_kind: PubListKind;
	}) => void;
}) {
	const [publisherId, setPublisherId] = useState("");
	const [errors, setErrors] = useState<Record<string, string>>({});

	const listKind = inferListKindFromMode(props.mode);

	function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		const parsed = addEntrySchema.safeParse({
			smartlink_id: props.smartlinkId,
			publisher_id: publisherId,
			list_kind: listKind,
		});
		if (!parsed.success) {
			setErrors(parsed.errors);
			return;
		}
		setErrors({});
		props.onSubmit(parsed.data);
		setPublisherId("");
	}

	return (
		<form onSubmit={handleSubmit} className={`${CARD} flex flex-col gap-4 p-5`} noValidate>
			<div className="flex flex-col gap-2">
				<label htmlFor="pub-list-publisher-id" className={LABEL}>
					Publisher ID
				</label>
				<input
					id="pub-list-publisher-id"
					name="publisher_id"
					type="text"
					autoComplete="off"
					placeholder="e.g. pub_3af21c"
					className={INPUT}
					value={publisherId}
					onChange={(e) => setPublisherId(e.currentTarget.value)}
					aria-invalid={Boolean(errors.publisher_id)}
				/>
				{errors.publisher_id && (
					<p className={ERROR_TEXT}>{errors.publisher_id}</p>
				)}
				{errors.smartlink_id && (
					<p className={ERROR_TEXT}>{errors.smartlink_id}</p>
				)}
				<p className={HINT}>
					Will be added as{" "}
					<span
						className={
							listKind === "allow"
								? "font-medium text-emerald-700"
								: "font-medium text-red-700"
						}
					>
						{listKind === "allow" ? "allow" : "deny"}
					</span>{" "}
					based on the current mode.
				</p>
			</div>
			<div className="flex items-center justify-end">
				<button type="submit" disabled={props.busy} className={PRIMARY_BTN}>
					{props.busy ? "Adding." : "Add entry"}
				</button>
			</div>
		</form>
	);
}

function EntriesTableCard(props: {
	entries: readonly PubListEntry[];
	busyDeleteId: string | null | undefined;
	onDelete: (id: string) => void;
	loading: boolean;
	canQuery: boolean;
}) {
	if (!props.canQuery) {
		return (
			<div className={`${CARD} p-5 text-sm text-zinc-500`}>
				Enter a SmartLink ID above to load its entries.
			</div>
		);
	}
	return (
		<div className={`${CARD} overflow-hidden`}>
			<div className="flex items-center justify-between border-b border-zinc-200 px-5 py-3">
				<p className={LABEL}>Current entries</p>
				<p className={HINT}>
					{props.entries.length} row
					{props.entries.length === 1 ? "" : "s"}
				</p>
			</div>
			{props.loading && props.entries.length === 0 ? (
				<div className="px-5 py-6 text-sm text-zinc-500">Loading.</div>
			) : props.entries.length === 0 ? (
				<div className="px-5 py-6 text-sm text-zinc-500">
					No entries yet. Add one above to start enforcing.
				</div>
			) : (
				<table className="w-full text-left text-sm">
					<thead className="bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500">
						<tr>
							<th className="px-5 py-2 font-medium">Publisher</th>
							<th className="px-5 py-2 font-medium">Kind</th>
							<th className="px-5 py-2 font-medium">Added</th>
							<th className="px-5 py-2" aria-label="Actions" />
						</tr>
					</thead>
					<tbody>
						{props.entries.map((e) => (
							<tr
								key={e.id}
								data-testid="pub-list-row"
								data-row-id={e.id}
								className="border-t border-zinc-100"
							>
								<td className="px-5 py-2 font-mono text-xs text-zinc-700">
									{e.publisher_id}
								</td>
								<td className="px-5 py-2">
									<KindBadge kind={e.list_kind} />
								</td>
								<td className="px-5 py-2 text-xs text-zinc-500">
									{formatCreatedAt(e.created_at)}
								</td>
								<td className="px-5 py-2 text-right">
									<button
										type="button"
										data-testid="pub-list-delete"
										onClick={() => props.onDelete(e.id)}
										disabled={props.busyDeleteId === e.id}
										className={GHOST_DANGER_BTN}
									>
										{props.busyDeleteId === e.id ? "Removing." : "Delete"}
									</button>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			)}
		</div>
	);
}

function KindBadge(props: { kind: PubListKind }) {
	const isAllow = props.kind === "allow";
	const base =
		"inline-flex items-center gap-1 rounded-[6px] border px-2 py-0.5 text-xs font-medium";
	const allow = "border-emerald-200 bg-emerald-50 text-emerald-700";
	const deny = "border-red-200 bg-red-50 text-red-700";
	return (
		<span className={`${base} ${isAllow ? allow : deny}`}>
			<span
				aria-hidden="true"
				className={`h-1.5 w-1.5 rounded-full ${
					isAllow ? "bg-emerald-500" : "bg-red-500"
				}`}
			/>
			{isAllow ? "Allow" : "Deny"}
		</span>
	);
}

function AccessTesterCard(props: {
	smartlinkId: string;
	busy: boolean;
	result: CheckResponse | null;
	onSubmit: (v: { smartlink_id: string; publisher_id: string }) => void;
}) {
	const [publisherId, setPublisherId] = useState("");
	const [errors, setErrors] = useState<Record<string, string>>({});

	function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		const parsed = checkSchema.safeParse({
			smartlink_id: props.smartlinkId,
			publisher_id: publisherId,
		});
		if (!parsed.success) {
			setErrors(parsed.errors);
			return;
		}
		setErrors({});
		props.onSubmit(parsed.data);
	}

	return (
		<form onSubmit={handleSubmit} className={`${CARD} flex flex-col gap-4 p-5`} noValidate>
			<div className="flex flex-col gap-2">
				<span className={LABEL}>Access tester</span>
				<p className={HINT}>
					Simulate a click from a publisher and see whether the current rules
					would allow or block it.
				</p>
			</div>
			<div className="flex flex-col gap-2">
				<label htmlFor="pub-list-test-publisher" className={LABEL}>
					Publisher ID to test
				</label>
				<input
					id="pub-list-test-publisher"
					name="test_publisher_id"
					type="text"
					autoComplete="off"
					placeholder="e.g. pub_3af21c"
					className={INPUT}
					value={publisherId}
					onChange={(e) => setPublisherId(e.currentTarget.value)}
					aria-invalid={Boolean(errors.publisher_id)}
				/>
				{errors.publisher_id && (
					<p className={ERROR_TEXT}>{errors.publisher_id}</p>
				)}
				{errors.smartlink_id && (
					<p className={ERROR_TEXT}>{errors.smartlink_id}</p>
				)}
			</div>
			<div className="flex items-center justify-between gap-4">
				{props.result ? (
					<AccessResultBadge result={props.result} />
				) : (
					<p className={HINT}>Result will show here once evaluated.</p>
				)}
				<button type="submit" disabled={props.busy} className={SECONDARY_BTN}>
					{props.busy ? "Checking." : "Evaluate"}
				</button>
			</div>
		</form>
	);
}

function AccessResultBadge(props: { result: CheckResponse }) {
	const { allowed, reason, mode } = props.result;
	const base =
		"inline-flex flex-col items-start gap-0.5 rounded-[6px] border px-3 py-2 text-sm";
	const allow = "border-emerald-200 bg-emerald-50 text-emerald-800";
	const deny = "border-red-200 bg-red-50 text-red-800";
	return (
		<div
			data-testid="pub-list-check-result"
			data-allowed={allowed}
			className={`${base} ${allowed ? allow : deny}`}
		>
			<span className="font-medium">
				{allowed ? "Allowed" : "Blocked"}
			</span>
			<span className="text-xs text-zinc-600">
				Mode: {mode}
				{reason ? ` — ${humanReason(reason)}` : ""}
			</span>
		</div>
	);
}

function humanReason(reason: string): string {
	switch (reason) {
		case "publisher_not_on_allow_list":
			return "publisher not on allow list";
		case "publisher_on_deny_list":
			return "publisher on deny list";
		default:
			return reason;
	}
}

function formatCreatedAt(ts: number): string {
	if (!ts || Number.isNaN(ts)) return "—";
	try {
		const d = new Date(ts * 1000);
		return d.toISOString().replace("T", " ").slice(0, 16) + " UTC";
	} catch {
		return "—";
	}
}
